<template lang="html">
  <section
    class="metric-highlight"
    :id="id"
    :draggable="draggable"
    @dragstart.stop="onComponentStartDrag($event)"
    @dragend.stop="onComponentEndDrag($event)"
    @dragover.prevent.stop
    @dragenter.stop="onComponentDragEnter($event)"
  >
    <div class="m-header">
      <div class="title">{{ $t("metricHighlight") }}</div>
      <settings-element
        @on-settings-edit-click="onSettingsClick"
        @on-settings-delete-click="onDeleteClick"
      />
    </div>

    <div class="card-description normal-wrap">
      {{ description }}
    </div>

    <div class="description-list horizontal" v-if="first">
      <div class="icon slds-icon_container" :class="getIconClass(first.icon)">
        <svg class="slds-icon">
          <use :xlink:href="getIconPath(first.icon)"></use>
        </svg>
      </div>
      <div class="text-container">
        <div class="label" :title="findExpLabel(first.label)">
          {{ getMessageText(messages.FIELD_LIST_ITEM_LABEL) }}:
          {{ findExpLabel(first.label) }}
        </div>
        <div class="value" :title="findExpLabel(first.value)">
          {{ getMessageText(messages.FIELD_LIST_ITEM_VALUE) }}:
          <span class="value-text"
            >{{ findExpLabel(first.value) }}
            <span v-if="first.valueAction"
              >, {{ getActionLabel(first.valueAction) }}</span
            >
          </span>
        </div>
      </div>
    </div>

    <div class="description-list horizontal" v-if="second">
      <div class="icon slds-icon_container" :class="getIconClass(second.icon)">
        <svg class="slds-icon">
          <use :xlink:href="getIconPath(second.icon)"></use>
        </svg>
      </div>
      <div class="text-container">
        <div class="label">
          {{ getMessageText(messages.FIELD_LIST_ITEM_LABEL) }}:
          {{ findExpLabel(second.label) }}
        </div>
        <div class="value">
          {{ getMessageText(messages.FIELD_LIST_ITEM_VALUE) }}:
          <span class="value-text"
            >{{ findExpLabel(second.value) }}
            <span v-if="second.valueAction"
              >, {{ getActionLabel(second.valueAction) }}</span
            >
          </span>
        </div>
      </div>
    </div>

    <simple-modal
      :show="displaySimpleModal"
      :message="getMessageText(messages.ARE_YOU_SURE)"
      :cancel-text="getMessageText(messages.CANCEL)"
      :ok-text="getMessageText(messages.OK)"
      @on-simple-modal-cancel="onCancelDeleteElement"
      @on-simple-modal-ok="onConfirmDeleteElement"
    />
  </section>
</template>

<script lang="js">
  import store from "@/store";
  import utils from "@/utils";
  import { MUTATION_NAME } from "@/static/global-variables";
  import expressionBuilder from "@/views/drag-drop-pages/components/utils/expression-builder";
  import dragAndDrop from "@/views/drag-drop-pages/components/utils/dragAndDrop";
  import SimpleModal from "@/views/drag-drop-pages/components/modal/SimpleModal";
  import SettingsElement from "@/views/drag-drop-pages/components/SettingsElement";

  export default  {
    name: "metric-highlight",
    components: {
      SimpleModal,
      SettingsElement
    },
    props: {
      id: {
        type: String
      },
      // props specific to draggable component
      allowDropzones: {
        type: Boolean,
        default: true
      },
      dropEffect: {
        type: String,
        default: dragAndDrop.DROP_EFFECT.MOVE
      },
      draggable: {
        type: Boolean,
        default: true
      },
      // props specific to this component below
      data: {
        type: Object
      },
      description: {
        type: String
      }
    },
    mounted () {
      this.setData(this.data);
    },
    data () {
      return {
        displaySimpleModal: false,
        first: null,
        second: null,
        staticType: expressionBuilder.EXPRESSION_TYPE.STATIC
      }
    },
    computed: {
      messages() {
        return store.state.messages;
      },
      deleteModal: {
        set(data) {
          store.dispatch(MUTATION_NAME.SET_DELETE_DRAGGABLE, data);
        },
        get() {
          return store.getters.deleteDraggableItem;
        }
      },
      editModal: {
        set(data) {
          store.dispatch(MUTATION_NAME.SET_EDIT_DRAGGABLE, data);
        },
        get() {
          return store.getters.editDraggableItem;
        }
      }
    },
    watch: {
      data: function() {
        this.setData(this.data);
      }
    },
    methods: {
      getIconClass(item) {
        return utils.getIconClass(item);
      },
      getIconPath(item) {
        return utils.getIconPath(item);
      },
      getActionLabel(action) {
        let output = "";
        if (action) {
          let count = action.navigation.type ? 1 : 0;
          count += action.dataActions? action.dataActions.length : 0;
          output = this.replaceMessageText(this.messages.USER_ACTION_COUNT, count);
        }

        return output;
      },
      findExpLabel(exp) {
        return utils.findExpressionLabel(exp);
      },
      setData(data) {
        if(data) {
          this.first = data.first;
          this.second = data.second;
        }
      },
      onCancelDeleteElement() {
        this.displaySimpleModal = false;
      },
      onConfirmDeleteElement() {
        this.displaySimpleModal = false;
        const obj = {
          id: this.id
        };
        this.deleteModal = {detail: obj};
      },
      onDeleteClick() {
        this.displaySimpleModal = true;
      },

      onSettingsClick() {
        const obj = {
          id: this.id,
          modal: "AddMetricModal",
          dropEffect: dragAndDrop.DROP_EFFECT.NONE,
          props: {
            description: this.description,
            first: JSON.parse(JSON.stringify(this.first)),
            second: JSON.parse(JSON.stringify(this.second))
          }
        };
        this.editModal = {detail: obj};
      },
      /**
       * @function getMetadata
       * @returns {Object} Metadata in JSON format
       */
      getMetadata() {
        // meta must contain meta.element object with at least meta.element.name, do not remove
        return {
          element: {
            name: this.$options.name // this field is required for all components
          },
          // add elements required for this component
          type: "Metric Highlight",
          description: this.description,
          first: this.first,
          second: this.second
        };
      },
      /**
       * @function deserialize
       * @param {Object} serializedJson - Metadata to create content of this component
       */
      deserialize(serializedJson) {
        this.setData({
          first: serializedJson.first,
          second: serializedJson.second
        });
        this.description = serializedJson.description;
      }
    }
  }
</script>

<style scoped lang="scss">
  @import "@/styles/components/main";
  @include drag-drop-a-tag;
  .metric-highlight {
    flex: 1;
    position: relative; /* component must be position relative */
    @include mixin-box;
    padding: 0.75rem;
    font-size: 0.8125rem;
    line-height: 1.5;
    color: #080707;
    min-width: 150px;
  }
  .m-shrink {
    @include mixin-shrink-layout;
  }

  .m-header {
    display: grid;
    box-sizing: border-box;
    grid-template-columns: auto 3rem;
    .title {
      font-weight: bold;
      font-size: 0.9rem;
    }
  }

  .description-list {
    display: flex;
    align-items: center;
    margin-bottom: 0.5rem;
    &.horizontal {
      .text-container {
        text-overflow: ellipsis;
        white-space: nowrap;
        overflow: hidden;
        padding-left: 0.25rem;
        padding-right: 0.25rem;
      }

      .label,
      .value {
        text-overflow: ellipsis;
        white-space: nowrap;
        overflow: hidden;
      }
      .value-text {
        font-weight: bold;
        font-size: 1rem;
      }
    }

    .slds-icon_container {
      width: 1.5rem;
      height: 1.5rem;
      margin-right: 0.5rem;

      .slds-icon {
        width: 1.5rem;
        height: 1.5rem;
      }
    }
  }
</style>
